import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  BarChart2,
  ChevronDown,
  ChevronUp,
  ClipboardCopy,
  Cloud,
  Copy,
  CheckCheck,
  Database,
  Download,
  Eye,
  ExternalLink,
  FlaskConical,
  GitMerge,
  History,
  Loader2,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Trash2,
  Trophy,
  UserPlus,
  X,
  Zap,
} from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassInput,
  GlassModal,
  GlassSelect,
  GlassTabs,
  ModuleHeader,
  EmailChipInput,
} from '../ui';
import {
  createABTest,
  createLeadForm,
  createNurtureWorkflow,
  deleteLeadForm,
  deployABVariants,
  enrollLeadInWorkflow,
  generateLeadForm,
  getABTestResults,
  getABTests,
  getCampaigns,
  getFormSubmissions,
  deleteFormSubmission,
  getLandingPages,
  getLeadCaptureSequences,
  getLeadForms,
  getNurtureWorkflows,
  getProducts,
  getWorkflowEnrollmentLog,
  promoteABWinner,
  startABTest,
  updateABTest,
  updateABTestAutoPromote,
  updateLeadForm,
  updateNurtureWorkflow,
  Product,
  NurtureWorkflowEnrollmentEvent,
} from '../../services/api';
import './LeadCapture.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OutletCtx {
  selectedProjectId?: string;
}

interface FormField {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  required: boolean;
  options?: string[];
}

interface LeadForm {
  form_id: string;
  project_id: string;
  name: string;
  description?: string;
  fields: FormField[];
  submit_button_text: string;
  success_message: string;
  crm_sync_enabled: boolean;
  crm_lead_source?: string;
  crm_field_mapping?: Record<string, string>;
  status: string;
  embed_code?: string;
  campaign_id?: string;
  campaign_name?: string;
  lead_magnet_product_id?: string;
  notification_emails?: string[];
  notification_digest?: boolean;
  submissions_count: number;
  created_at?: string;
}

interface ABTestItem {
  test_id: string;
  name: string;
  landing_page_id_a: string;
  landing_page_id_b: string;
  page_a_name?: string;
  page_b_name?: string;
  status: string;
  traffic_split: number;
  winner?: string;
  variant_a_url?: string;
  variant_b_url?: string;
  variant_a_visits?: number;
  variant_b_visits?: number;
  variant_a_conversions?: number;
  variant_b_conversions?: number;
  auto_promote?: boolean;
  concluded_at?: string;
  created_at?: string;
}

interface ABTestResults {
  test_id: string;
  status: string;
  variant_a: { visits: number; conversions: number; rate: number; url?: string };
  variant_b: { visits: number; conversions: number; rate: number; url?: string };
  total_visits: number;
  significant: boolean;
  recommended_winner: 'a' | 'b' | null;
  winner: string | null;
  concluded_at: string | null;
}

interface Workflow {
  workflow_id: string;
  name: string;
  description?: string;
  trigger: string;
  email_sequence_id?: string;
  email_sequence_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  status: string;
  leads_enrolled: number;
  last_triggered_at?: string | null;
  created_at?: string;
}

interface Submission {
  submission_id: string;
  form_id: string;
  data: Record<string, string>;
  crm_lead_id?: string;
  crm_synced_at?: string;
  submitted_at?: string;
  ip_address?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'forms', label: 'Forms' },
  { id: 'builder', label: 'Form Builder' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'abtests', label: 'A/B Tests' },
  { id: 'workflows', label: 'Workflows' },
];

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown' },
];

const TRIGGER_OPTIONS = [
  { value: 'form_submit', label: 'On Form Submit' },
  { value: 'lead_created', label: 'On Lead Created' },
  { value: 'manual', label: 'Manual' },
];

// Triggers that campaign_enrollment_service.enroll_from_campaign_trigger actually
// matches on (mirrors backend campaign_enrollment_service.FIRING_TRIGGERS). A
// workflow on any other trigger ('manual', 'autonomous_enrollment') is never
// auto-fired by this feature, so the "Not armed" chip does not apply to it.
const FIRING_TRIGGERS = ['form_submit', 'lead_created'];

const OUTCOME_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
  enrolled: 'success',
  duplicate: 'info',
  suppressed: 'warning',
  no_sequence: 'warning',
  error: 'danger',
};

const CRM_FIELD_OPTIONS = [
  { value: '', label: 'Not mapped' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email1', label: 'Email' },
  { value: 'phone_work', label: 'Phone (Work)' },
  { value: 'phone_mobile', label: 'Phone (Mobile)' },
  { value: 'account_name', label: 'Company' },
  { value: 'title', label: 'Job Title' },
  { value: 'description', label: 'Description' },
  { value: 'lead_source', label: 'Lead Source' },
];

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
  active: 'success',
  running: 'success',
  completed: 'info',
  paused: 'warning',
  inactive: 'default',
  draft: 'default',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function newField(): FormField {
  return {
    id: `field_${Date.now()}`,
    type: 'text',
    label: '',
    placeholder: '',
    required: false,
  };
}

// ---------------------------------------------------------------------------
// Enroll Lead Modal
// ---------------------------------------------------------------------------

interface EnrollModalProps {
  workflow: Workflow;
  onClose: () => void;
  onEnrolled: () => void;
}

const EnrollModal: React.FC<EnrollModalProps> = ({ workflow, onClose, onEnrolled }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEnroll = async () => {
    if (!email && !lastName) { setError('Please provide at least an email or last name.'); return; }
    setLoading(true);
    setError('');
    try {
      await enrollLeadInWorkflow(workflow.workflow_id, { first_name: firstName, last_name: lastName, email, company });
      onEnrolled();
      onClose();
    } catch (e: any) {
      setError(getApiError(e, 'Failed to enroll lead'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel-sheet" onClick={e => e.stopPropagation()}>
        <div className="panel-sheet-header">
          <h2 className="panel-sheet-title">Enroll Lead — {workflow.name}</h2>
          <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} />
        </div>
        <div className="enroll-form">
          <div className="form-row">
            <GlassInput label="First Name" value={firstName} onChange={setFirstName} placeholder="John" />
            <GlassInput label="Last Name" value={lastName} onChange={setLastName} placeholder="Doe" />
          </div>
          <GlassInput label="Email" type="email" value={email} onChange={setEmail} placeholder="john@example.com" />
          <GlassInput label="Company" value={company} onChange={setCompany} placeholder="Acme Corp" />
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}
        </div>
        <div className="panel-sheet-footer">
          <GlassButton variant="secondary" onClick={onClose}>Cancel</GlassButton>
          <GlassButton variant="primary" loading={loading} icon={<UserPlus size={16} />} onClick={handleEnroll}>
            Enroll Lead
          </GlassButton>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form Builder Panel
// ---------------------------------------------------------------------------

interface FormBuilderProps {
  projectId: string;
  campaigns: { campaign_id: string; name: string }[];
  editingForm?: LeadForm | null;
  onSaved: (form: LeadForm) => void;
  onCancel: () => void;
}

const FormBuilderPanel: React.FC<FormBuilderProps> = ({ projectId, campaigns, editingForm, onSaved, onCancel }) => {
  const [name, setName] = useState(editingForm?.name || '');
  const [description, setDescription] = useState(editingForm?.description || '');
  const [fields, setFields] = useState<FormField[]>(editingForm?.fields || []);
  const [submitText, setSubmitText] = useState(editingForm?.submit_button_text || 'Submit');
  const [successMsg, setSuccessMsg] = useState(editingForm?.success_message || "Thank you! We'll be in touch.");
  const [crmSync, setCrmSync] = useState(editingForm?.crm_sync_enabled ?? true);
  const [crmSource, setCrmSource] = useState(editingForm?.crm_lead_source || '');
  const [notificationEmails, setNotificationEmails] = useState<string[]>(editingForm?.notification_emails || []);
  const [notificationDigest, setNotificationDigest] = useState<boolean>(!!editingForm?.notification_digest);
  const [campaignId, setCampaignId] = useState(editingForm?.campaign_id || '');
  const [crmFieldMapping, setCrmFieldMapping] = useState<Record<string, string>>(editingForm?.crm_field_mapping || {});
  const [showPreview, setShowPreview] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [industry, setIndustry] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedForm, setSavedForm] = useState<LeadForm | null>(null);
  const [copied, setCopied] = useState(false);

  // Lead magnet state
  const [leadMagnetEnabled, setLeadMagnetEnabled] = useState(!!editingForm?.lead_magnet_product_id);
  const [leadMagnetProductId, setLeadMagnetProductId] = useState(editingForm?.lead_magnet_product_id || '');
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);

  useEffect(() => {
    if (leadMagnetEnabled && availableProducts.length === 0) {
      setProductsLoading(true);
      getProducts()
        .then(data => setAvailableProducts(Array.isArray(data) ? data : []))
        .catch(() => {})
        .finally(() => setProductsLoading(false));
    }
  }, [leadMagnetEnabled, availableProducts.length]);

  const selectedLeadMagnetProduct = availableProducts.find(p => p.product_id === leadMagnetProductId);

  const campaignOptions = [
    { value: '', label: '— None —' },
    ...campaigns.map(c => ({ value: c.campaign_id, label: c.name })),
  ];

  const handleGenerateFields = async () => {
    if (!purpose) { setError('Enter a purpose first'); return; }
    setGenerating(true);
    setError('');
    try {
      const result = await generateLeadForm({ purpose, industry, campaign_id: campaignId || undefined });
      if (result?.fields?.length) {
        setFields(result.fields);
      }
    } catch (e: any) {
      setError(getApiError(e, 'AI generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  const addField = () => setFields(prev => [...prev, newField()]);

  const removeField = (idx: number) => setFields(prev => prev.filter((_, i) => i !== idx));

  const moveField = (idx: number, direction: 'up' | 'down') => {
    setFields(prev => {
      const next = [...prev];
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= next.length) return next;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const updateField = (idx: number, patch: Partial<FormField>) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  const updateCrmMapping = (fieldId: string, crmField: string) => {
    setCrmFieldMapping(prev => {
      const next = { ...prev };
      if (crmField) {
        next[fieldId] = crmField;
      } else {
        delete next[fieldId];
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Form name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        project_id: projectId,
        name,
        description,
        fields,
        submit_button_text: submitText,
        success_message: successMsg,
        crm_sync_enabled: crmSync,
        crm_lead_source: crmSource || undefined,
        crm_field_mapping: Object.keys(crmFieldMapping).length > 0 ? crmFieldMapping : undefined,
        campaign_id: campaignId || undefined,
        lead_magnet_product_id: leadMagnetEnabled && leadMagnetProductId ? leadMagnetProductId : undefined,
        notification_emails: notificationEmails,
        notification_digest: notificationDigest,
      };
      let result: LeadForm;
      if (editingForm) {
        result = await updateLeadForm(editingForm.form_id, payload);
      } else {
        result = await createLeadForm(payload);
      }
      setSavedForm(result);
      onSaved(result);
    } catch (e: any) {
      setError(getApiError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmbed = () => {
    if (!savedForm?.embed_code) return;
    navigator.clipboard.writeText(savedForm.embed_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="form-builder">
      {/* Basic info */}
      <div className="form-builder-section">
        <h3 className="form-builder-section-title">Form Details</h3>
        <div className="form-row">
          <GlassInput label="Form Name *" value={name} onChange={setName} placeholder="e.g. Contact Request Form" />
          <GlassSelect
            label="Link to Campaign (optional)"
            options={campaignOptions}
            value={campaignId}
            onChange={setCampaignId}
          />
        </div>
        <GlassInput label="Description" value={description} onChange={setDescription} placeholder="Brief description" />
      </div>

      {/* AI generation */}
      <div className="form-builder-section">
        <h3 className="form-builder-section-title"><Zap size={14} /> AI Form Generator</h3>
        <div className="form-row">
          <GlassInput label="Purpose" value={purpose} onChange={setPurpose} placeholder="e.g. Free consultation request" />
          <GlassInput label="Industry" value={industry} onChange={setIndustry} placeholder="e.g. Legal, Real Estate" />
        </div>
        <GlassButton
          variant="secondary"
          size="sm"
          icon={generating ? <Loader2 size={14} /> : <Zap size={14} />}
          loading={generating}
          onClick={handleGenerateFields}
        >
          Generate Fields with AI
        </GlassButton>
      </div>

      {/* Fields */}
      <div className="form-builder-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="form-builder-section-title">Form Fields ({fields.length})</h3>
          <GlassButton variant="secondary" size="sm" icon={<Plus size={14} />} onClick={addField}>
            Add Field
          </GlassButton>
        </div>
        <div className="field-list">
          {fields.map((field, idx) => (
            <div key={field.id} className="field-item">
              <div className="field-item-header">
                <span className="field-item-label">Field {idx + 1}</span>
                <div className="field-item-controls">
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    icon={<ChevronUp size={14} />}
                    onClick={() => moveField(idx, 'up')}
                    disabled={idx === 0}
                    title="Move up"
                  />
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    icon={<ChevronDown size={14} />}
                    onClick={() => moveField(idx, 'down')}
                    disabled={idx === fields.length - 1}
                    title="Move down"
                  />
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    icon={<Trash2 size={14} />}
                    onClick={() => removeField(idx)}
                    title="Remove field"
                  />
                </div>
              </div>
              <div className="field-row field-row-4col">
                <GlassSelect
                  label="Type"
                  options={FIELD_TYPES}
                  value={field.type}
                  onChange={v => updateField(idx, { type: v })}
                />
                <GlassInput
                  label="Label"
                  value={field.label}
                  onChange={v => updateField(idx, { label: v })}
                  placeholder="Field label"
                />
                <GlassInput
                  label="Placeholder"
                  value={field.placeholder}
                  onChange={v => updateField(idx, { placeholder: v })}
                  placeholder="Placeholder text"
                />
                <GlassSelect
                  label="CRM Field"
                  options={CRM_FIELD_OPTIONS}
                  value={crmFieldMapping[field.id] || ''}
                  onChange={v => updateCrmMapping(field.id, v)}
                  size="sm"
                />
              </div>
              {field.type === 'select' && (
                <GlassInput
                  label="Options (comma-separated)"
                  value={(field.options || []).join(', ')}
                  onChange={v => updateField(idx, { options: v.split(',').map(s => s.trim()).filter(Boolean) })}
                  placeholder="Option 1, Option 2, Option 3"
                />
              )}
              <label className="field-required-toggle">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={e => updateField(idx, { required: e.target.checked })}
                />
                Required
              </label>
            </div>
          ))}
          {fields.length === 0 && (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
              No fields yet. Add fields or use AI generation above.
            </div>
          )}
        </div>
      </div>

      {/* Submission behaviour */}
      <div className="form-builder-section">
        <h3 className="form-builder-section-title">Submission Behaviour</h3>
        <div className="form-row">
          <GlassInput label="Submit Button Text" value={submitText} onChange={setSubmitText} placeholder="Submit" />
          <GlassInput label="Success Message" value={successMsg} onChange={setSuccessMsg} placeholder="Thank you!" />
        </div>
        <div className="toggle-row">
          <span className="toggle-label">Sync submissions to SuiteCRM</span>
          <label className="toggle-switch">
            <input type="checkbox" checked={crmSync} onChange={e => setCrmSync(e.target.checked)} />
            <span className="toggle-slider" />
          </label>
        </div>
        {crmSync && (
          <GlassInput label="CRM Lead Source" value={crmSource} onChange={setCrmSource} placeholder="e.g. Web Site, Campaign" />
        )}
      </div>

      {/* Notifications */}
      <div className="form-builder-section">
        <h3 className="form-builder-section-title">Lead Notifications</h3>
        <EmailChipInput
          label="Recipients"
          value={notificationEmails}
          onChange={setNotificationEmails}
          placeholder="alice@example.com, then Enter"
          helperText="Leave blank to use the project default. Add multiple recipients separated by Enter or comma."
        />
        <div className="toggle-row" style={{ marginTop: 'var(--space-3)' }}>
          <span className="toggle-label">Batch into a daily digest instead of sending one email per lead</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={notificationDigest}
              onChange={e => setNotificationDigest(e.target.checked)}
            />
            <span className="toggle-slider" />
          </label>
        </div>
      </div>

      {/* Lead Magnet */}
      <div className="form-builder-section">
        <h3 className="form-builder-section-title"><Download size={14} /> Free Download on Submission</h3>
        <div className="toggle-row">
          <span className="toggle-label">Deliver a free product when this form is submitted</span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={leadMagnetEnabled}
              onChange={e => {
                setLeadMagnetEnabled(e.target.checked);
                if (!e.target.checked) setLeadMagnetProductId('');
              }}
            />
            <span className="toggle-slider" />
          </label>
        </div>

        {leadMagnetEnabled && (
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {productsLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading products…
              </div>
            ) : availableProducts.length === 0 ? (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                No products found. Create a product in the Marketplace module first.
              </p>
            ) : (
              <GlassSelect
                label="Select Product to Deliver"
                options={[
                  { value: '', label: '— Choose a product —' },
                  ...availableProducts.map(p => ({
                    value: p.product_id,
                    label: p.price != null
                      ? `${p.name} (${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.price)})`
                      : p.name,
                  })),
                ]}
                value={leadMagnetProductId}
                onChange={setLeadMagnetProductId}
              />
            )}

            {selectedLeadMagnetProduct && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)',
                padding: '6px 12px',
                background: 'var(--primary-50)',
                border: '1px solid var(--primary-200)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-sm)',
                color: 'var(--primary-600)',
                width: 'fit-content',
              }}>
                <Download size={13} />
                Will deliver: {selectedLeadMagnetProduct.name}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Live Form Preview */}
      <div className="form-builder-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="form-builder-section-title"><Eye size={14} /> Live Preview</h3>
          <GlassButton
            variant="secondary"
            size="sm"
            icon={<Eye size={14} />}
            onClick={() => setShowPreview(prev => !prev)}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </GlassButton>
        </div>
        {showPreview && (
          <div className="form-preview">
            <div className="form-preview-card">
              {name && <h3 className="form-preview-title">{name}</h3>}
              {description && <p className="form-preview-description">{description}</p>}
              {fields.length === 0 ? (
                <p className="form-preview-empty">Add fields above to see a preview.</p>
              ) : (
                <div className="form-preview-fields">
                  {fields.map(field => (
                    <div key={field.id} className="form-preview-field">
                      <label className="form-preview-label">
                        {field.label || 'Untitled field'}
                        {field.required && <span className="form-preview-required">*</span>}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          className="form-preview-input form-preview-textarea"
                          placeholder={field.placeholder}
                          disabled
                          rows={3}
                        />
                      ) : field.type === 'select' ? (
                        <select className="form-preview-input form-preview-select" disabled>
                          <option>{field.placeholder || 'Select...'}</option>
                          {(field.options || []).map((opt, i) => (
                            <option key={i}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="form-preview-input"
                          type={field.type}
                          placeholder={field.placeholder}
                          disabled
                        />
                      )}
                      {crmFieldMapping[field.id] && (
                        <span className="form-preview-crm-tag">
                          <Database size={10} />
                          {CRM_FIELD_OPTIONS.find(o => o.value === crmFieldMapping[field.id])?.label || crmFieldMapping[field.id]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button className="form-preview-submit" disabled>
                {submitText || 'Submit'}
              </button>
              {successMsg && (
                <p className="form-preview-success-hint">
                  On submit: "{successMsg}"
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <GlassButton variant="secondary" onClick={onCancel}>Cancel</GlassButton>
        <GlassButton variant="primary" loading={saving} onClick={handleSave}>
          {editingForm ? 'Save Changes' : 'Create Form'}
        </GlassButton>
      </div>

      {/* Embed code after save */}
      {savedForm?.embed_code && (
        <div className="form-builder-section">
          <h3 className="form-builder-section-title"><ClipboardCopy size={14} /> Embed Code</h3>
          <div className="embed-code-wrapper">
            <div className="embed-copy-bar">
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                Copy this snippet to embed the form on any website.
              </span>
              <GlassButton
                variant="secondary"
                size="sm"
                icon={<ClipboardCopy size={14} />}
                onClick={handleCopyEmbed}
              >
                Copy
              </GlassButton>
              {copied && <span className="copy-feedback">Copied!</span>}
            </div>
            <pre className="embed-code-block">{savedForm.embed_code}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Module
// ---------------------------------------------------------------------------

const LeadCaptureModule: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const projectId = ctx?.selectedProjectId || '';

  const [activeTab, setActiveTab] = useState('forms');
  const [forms, setForms] = useState<LeadForm[]>([]);
  const [abTests, setAbTests] = useState<ABTestItem[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [campaigns, setCampaigns] = useState<{ campaign_id: string; name: string }[]>([]);
  const [landingPages, setLandingPages] = useState<{ landing_page_id: string; title: string }[]>([]);

  // Submissions state
  const [selectedFormForSubs, setSelectedFormForSubs] = useState('');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [subTotal, setSubTotal] = useState(0);
  const [subSkip, setSubSkip] = useState(0);
  const subLimit = 25;

  // Delete-submission confirm modal
  const [pendingDelete, setPendingDelete] = useState<Submission | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Loading / error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Builder state
  const [editingForm, setEditingForm] = useState<LeadForm | null>(null);

  // Embed copy for list
  const [copiedFormId, setCopiedFormId] = useState('');

  // A/B test create modal
  const [showABModal, setShowABModal] = useState(false);
  const [abName, setAbName] = useState('');
  const [abPageA, setAbPageA] = useState('');
  const [abPageB, setAbPageB] = useState('');
  const [abSplit, setAbSplit] = useState(50);
  const [abSaving, setAbSaving] = useState(false);
  const [abError, setAbError] = useState('');

  // A/B test lifecycle state
  const [deployingTestId, setDeployingTestId] = useState<string | null>(null);
  const [startingTestId, setStartingTestId] = useState<string | null>(null);
  const [promotingTestId, setPromotingTestId] = useState<string | null>(null);
  const [abResults, setAbResults] = useState<Record<string, ABTestResults>>({});
  const [loadingResultsFor, setLoadingResultsFor] = useState<string | null>(null);
  const [copiedAbUrl, setCopiedAbUrl] = useState<string | null>(null);
  const [expandedTestId, setExpandedTestId] = useState<string | null>(null);

  // Workflow create modal
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [wfName, setWfName] = useState('');
  const [wfDescription, setWfDescription] = useState('');
  const [wfTrigger, setWfTrigger] = useState('manual');
  const [wfCampaignId, setWfCampaignId] = useState('');
  const [wfSeqId, setWfSeqId] = useState('');
  const [wfSaving, setWfSaving] = useState(false);
  const [wfError, setWfError] = useState('');
  const [sequences, setSequences] = useState<{ sequence_id: string; name: string }[]>([]);

  // Workflow edit modal
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [ewName, setEwName] = useState('');
  const [ewDescription, setEwDescription] = useState('');
  const [ewTrigger, setEwTrigger] = useState('manual');
  const [ewCampaignId, setEwCampaignId] = useState('');
  const [ewSeqId, setEwSeqId] = useState('');
  const [ewStatus, setEwStatus] = useState('draft');
  const [ewSaving, setEwSaving] = useState(false);
  const [ewError, setEwError] = useState('');

  // Enroll lead modal
  const [enrollWorkflow, setEnrollWorkflow] = useState<Workflow | null>(null);

  // Enrollment-log panel
  const [logWorkflow, setLogWorkflow] = useState<Workflow | null>(null);
  const [logEvents, setLogEvents] = useState<NurtureWorkflowEnrollmentEvent[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadForms = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getLeadForms(projectId);
      setForms(data);
    } catch (e) {
      console.error('Failed to load forms', e);
    }
  }, [projectId]);

  const loadABTests = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getABTests(projectId);
      setAbTests(data);
    } catch (e) {
      console.error('Failed to load A/B tests', e);
    }
  }, [projectId]);

  const loadWorkflows = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getNurtureWorkflows(projectId);
      setWorkflows(data);
    } catch (e) {
      console.error('Failed to load workflows', e);
    }
  }, [projectId]);

  const loadCampaigns = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getCampaigns(projectId);
      setCampaigns(data);
    } catch (e) {
      console.error('Failed to load campaigns', e);
    }
  }, [projectId]);

  const loadLandingPages = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getLandingPages(projectId);
      setLandingPages(data);
    } catch (e) {
      console.error('Failed to load landing pages', e);
    }
  }, [projectId]);

  const loadSequences = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getLeadCaptureSequences(projectId);
      setSequences(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load sequences', e);
    }
  }, [projectId]);

  const loadSubmissions = useCallback(async () => {
    if (!selectedFormForSubs) return;
    try {
      const result = await getFormSubmissions(selectedFormForSubs);
      if (result && 'submissions' in result) {
        setSubmissions(result.submissions);
        setSubTotal(result.total);
      } else {
        setSubmissions(Array.isArray(result) ? result : []);
      }
    } catch (e) {
      console.error('Failed to load submissions', e);
    }
  }, [selectedFormForSubs]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([loadForms(), loadCampaigns(), loadLandingPages(), loadSequences()])
      .finally(() => setLoading(false));
  }, [projectId, loadForms, loadCampaigns, loadLandingPages, loadSequences]);

  useEffect(() => {
    if (activeTab === 'abtests') loadABTests();
    if (activeTab === 'workflows') loadWorkflows();
  }, [activeTab, loadABTests, loadWorkflows]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions, selectedFormForSubs]);

  // ── Handlers — Forms ──────────────────────────────────────────────────────

  const handleFormSaved = (form: LeadForm) => {
    setForms(prev => {
      const exists = prev.find(f => f.form_id === form.form_id);
      if (exists) return prev.map(f => f.form_id === form.form_id ? form : f);
      return [form, ...prev];
    });
  };

  const handleDeleteForm = async (formId: string) => {
    if (!window.confirm('Delete this form and all its submissions?')) return;
    try {
      await deleteLeadForm(formId);
      setForms(prev => prev.filter(f => f.form_id !== formId));
    } catch (e: any) {
      setError(getApiError(e, 'Delete failed'));
    }
  };

  const handleToggleFormStatus = async (form: LeadForm) => {
    const newStatus = form.status === 'active' ? 'inactive' : 'active';
    try {
      const updated = await updateLeadForm(form.form_id, { status: newStatus });
      setForms(prev => prev.map(f => f.form_id === form.form_id ? updated : f));
    } catch (e: any) {
      setError(getApiError(e, 'Update failed'));
    }
  };

  const handleCopyEmbed = (form: LeadForm) => {
    if (!form.embed_code) return;
    navigator.clipboard.writeText(form.embed_code).then(() => {
      setCopiedFormId(form.form_id);
      setTimeout(() => setCopiedFormId(''), 2000);
    });
  };

  // ── Handlers — A/B Tests ──────────────────────────────────────────────────

  const handleCreateABTest = async () => {
    if (!abName || !abPageA || !abPageB) { setAbError('All fields required'); return; }
    if (abPageA === abPageB) { setAbError('Variant A and B must be different pages'); return; }
    setAbSaving(true);
    setAbError('');
    try {
      const result = await createABTest({
        project_id: projectId,
        name: abName,
        landing_page_id_a: abPageA,
        landing_page_id_b: abPageB,
        traffic_split: abSplit,
      });
      setAbTests(prev => [result, ...prev]);
      setShowABModal(false);
      setAbName(''); setAbPageA(''); setAbPageB(''); setAbSplit(50);
    } catch (e: any) {
      setAbError(getApiError(e, 'Failed to create test'));
    } finally {
      setAbSaving(false);
    }
  };

  const handleABAction = async (test: ABTestItem, action: 'start' | 'pause' | 'complete') => {
    const statusMap = { start: 'running', pause: 'paused', complete: 'completed' };
    try {
      const updated = await updateABTest(test.test_id, { status: statusMap[action] });
      setAbTests(prev => prev.map(t => t.test_id === test.test_id ? updated : t));
    } catch (e: any) {
      setError(getApiError(e, 'Update failed'));
    }
  };

  const handleDeclareWinner = async (test: ABTestItem, winner: 'a' | 'b') => {
    try {
      const updated = await updateABTest(test.test_id, { winner, status: 'completed' });
      setAbTests(prev => prev.map(t => t.test_id === test.test_id ? updated : t));
    } catch (e: any) {
      setError(getApiError(e, 'Update failed'));
    }
  };

  // H5.4 A/B lifecycle handlers

  const handleDeployVariants = async (test: ABTestItem) => {
    setDeployingTestId(test.test_id);
    try {
      const result = await deployABVariants(test.test_id);
      setAbTests(prev => prev.map(t =>
        t.test_id === test.test_id
          ? { ...t, variant_a_url: result.variant_a_url, variant_b_url: result.variant_b_url }
          : t
      ));
    } catch (e: any) {
      setError(getApiError(e, 'Deploy variants failed'));
    } finally {
      setDeployingTestId(null);
    }
  };

  const handleStartTest = async (test: ABTestItem) => {
    setStartingTestId(test.test_id);
    try {
      const updated = await startABTest(test.test_id, test.traffic_split);
      setAbTests(prev => prev.map(t => t.test_id === test.test_id ? updated : t));
    } catch (e: any) {
      setError(getApiError(e, 'Start test failed'));
    } finally {
      setStartingTestId(null);
    }
  };

  const handleLoadResults = async (testId: string) => {
    setLoadingResultsFor(testId);
    try {
      const results = await getABTestResults(testId);
      setAbResults(prev => ({ ...prev, [testId]: results }));
      setExpandedTestId(testId);
    } catch (e: any) {
      setError('Failed to load results');
    } finally {
      setLoadingResultsFor(null);
    }
  };

  const handlePromoteWinner = async (test: ABTestItem, variant: 'a' | 'b') => {
    if (!window.confirm(`Promote Variant ${variant.toUpperCase()} as the winner of "${test.name}"?`)) return;
    setPromotingTestId(test.test_id);
    try {
      const updated = await promoteABWinner(test.test_id, variant);
      setAbTests(prev => prev.map(t => t.test_id === test.test_id ? updated : t));
    } catch (e: any) {
      setError(getApiError(e, 'Promote winner failed'));
    } finally {
      setPromotingTestId(null);
    }
  };

  const handleToggleAutoPromote = async (test: ABTestItem) => {
    try {
      const updated = await updateABTestAutoPromote(test.test_id, !test.auto_promote);
      setAbTests(prev => prev.map(t => t.test_id === test.test_id ? updated : t));
    } catch (e: any) {
      setError('Failed to update auto-promote setting');
    }
  };

  const handleCopyAbUrl = (url: string, key: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedAbUrl(key);
      setTimeout(() => setCopiedAbUrl(null), 2000);
    });
  };

  // ── Handlers — Workflows ──────────────────────────────────────────────────

  const handleCreateWorkflow = async () => {
    if (!wfName) { setWfError('Workflow name is required'); return; }
    setWfSaving(true);
    setWfError('');
    try {
      const result = await createNurtureWorkflow({
        project_id: projectId,
        name: wfName,
        description: wfDescription,
        trigger: wfTrigger,
        campaign_id: wfCampaignId || undefined,
        email_sequence_id: wfSeqId || undefined,
      });
      setWorkflows(prev => [result, ...prev]);
      setShowWorkflowModal(false);
      setWfName(''); setWfDescription(''); setWfTrigger('manual'); setWfCampaignId(''); setWfSeqId('');
    } catch (e: any) {
      setWfError(getApiError(e, 'Failed to create workflow'));
    } finally {
      setWfSaving(false);
    }
  };

  const handleWorkflowToggle = async (wf: Workflow) => {
    const newStatus = wf.status === 'active' ? 'paused' : 'active';
    try {
      const updated = await updateNurtureWorkflow(wf.workflow_id, { status: newStatus });
      setWorkflows(prev => prev.map(w => w.workflow_id === wf.workflow_id ? updated : w));
    } catch (e: any) {
      setError(getApiError(e, 'Update failed'));
    }
  };

  const handleOpenEditWorkflow = (wf: Workflow) => {
    setEditingWorkflow(wf);
    setEwName(wf.name);
    setEwDescription(wf.description || '');
    setEwTrigger(wf.trigger);
    setEwCampaignId(wf.campaign_id || '');
    setEwSeqId(wf.email_sequence_id || '');
    setEwStatus(wf.status);
    setEwError('');
  };

  const handleUpdateWorkflow = async () => {
    if (!editingWorkflow) return;
    if (!ewName.trim()) { setEwError('Workflow name is required'); return; }
    setEwSaving(true);
    setEwError('');
    try {
      const updated = await updateNurtureWorkflow(editingWorkflow.workflow_id, {
        name: ewName,
        description: ewDescription,
        trigger: ewTrigger,
        // Explicit null (not undefined) so choosing "— None —" actually
        // unlinks the field — the backend's `exclude_unset=True` only omits
        // a key that was never present in the request body at all.
        campaign_id: ewCampaignId || null,
        email_sequence_id: ewSeqId || null,
        status: ewStatus,
      });
      setWorkflows(prev => prev.map(w => w.workflow_id === updated.workflow_id ? updated : w));
      setEditingWorkflow(null);
    } catch (e: any) {
      setEwError(getApiError(e, 'Failed to update workflow'));
    } finally {
      setEwSaving(false);
    }
  };

  const handleViewEnrollmentLog = async (wf: Workflow) => {
    setLogWorkflow(wf);
    setLogEvents([]);
    setLogError('');
    setLogLoading(true);
    try {
      const result = await getWorkflowEnrollmentLog(wf.workflow_id);
      setLogEvents(result.events || []);
    } catch (e: any) {
      setLogError(getApiError(e, 'Failed to load enrollment log'));
    } finally {
      setLogLoading(false);
    }
  };

  /** "Not armed" — the workflow is switched on but structurally cannot fire:
   * its trigger is one campaign_enrollment_service actually matches on
   * (FIRING_TRIGGERS), yet it is missing the campaign or sequence link that
   * trigger needs (or the sequence was deleted out from under it — see the
   * edm_routes.delete_sequence gotcha in the spec, which nulls
   * email_sequence_id and leaves status untouched). A 'manual'/
   * 'autonomous_enrollment' trigger workflow, or one that isn't 'active', is
   * never "not armed" — it was never going to auto-fire in the first place. */
  const isWorkflowNotArmed = (wf: Workflow): boolean =>
    wf.status === 'active' &&
    FIRING_TRIGGERS.includes(wf.trigger) &&
    (!wf.campaign_id || !wf.email_sequence_id);

  // ── No project selected guard ─────────────────────────────────────────────

  if (!projectId) {
    return (
      <div className="lead-capture-module">
        <ModuleHeader title="Lead Forms" breadcrumb={['Lead Forms']} />
        <GlassCard>
          <div className="empty-state">
            <UserPlus size={40} className="empty-state-icon" />
            <h3 className="empty-state-title">Select a project</h3>
            <p className="empty-state-desc">Choose a project from the sidebar to manage lead forms.</p>
          </div>
        </GlassCard>
      </div>
    );
  }

  // ── Tabs content ──────────────────────────────────────────────────────────

  const formOptions = forms.map(f => ({ value: f.form_id, label: f.name }));
  const pageOptions = [
    { value: '', label: '— Select page —' },
    ...landingPages.map(p => ({ value: p.landing_page_id, label: p.title })),
  ];
  const campaignOptions = [
    { value: '', label: '— None —' },
    ...campaigns.map(c => ({ value: c.campaign_id, label: c.name })),
  ];
  const seqOptions = [
    { value: '', label: '— None —' },
    ...sequences.map(s => ({ value: s.sequence_id, label: s.name })),
  ];

  return (
    <div className="lead-capture-module">
      <ModuleHeader
        title="Lead Forms"
        breadcrumb={['Lead Forms']}
        actions={[
          <GlassButton
            key="refresh"
            variant="ghost"
            size="sm"
            icon={<RefreshCw size={16} />}
            onClick={() => {
              loadForms();
              if (activeTab === 'abtests') loadABTests();
              if (activeTab === 'workflows') loadWorkflows();
            }}
            title="Refresh"
          />,
        ]}
      />

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)', padding: 'var(--space-3) var(--space-4)', background: 'var(--panel)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)' }}>
          <AlertCircle size={14} /> {error}
          <GlassButton variant="ghost" size="sm" icon={<X size={14} />} onClick={() => setError('')} />
        </div>
      )}

      <div className="lead-capture-tabs-bar">
        <GlassTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* ── Tab: Forms list ─────────────────────────────────────────────── */}
      {activeTab === 'forms' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <GlassButton
              variant="primary"
              icon={<Plus size={16} />}
              onClick={() => { setEditingForm(null); setActiveTab('builder'); }}
            >
              Create Form
            </GlassButton>
          </div>
          {loading ? (
            <div className="empty-state"><Loader2 size={24} className="empty-state-icon" /></div>
          ) : forms.length === 0 ? (
            <GlassCard>
              <div className="empty-state">
                <UserPlus size={40} className="empty-state-icon" />
                <h3 className="empty-state-title">No forms yet</h3>
                <p className="empty-state-desc">Create a lead capture form to start collecting leads.</p>
                <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setActiveTab('builder')}>
                  Create Form
                </GlassButton>
              </div>
            </GlassCard>
          ) : (
            <div className="forms-grid">
              {forms.map(form => (
                <GlassCard key={form.form_id}>
                  <div className="form-card">
                    <div className="form-card-header">
                      <h3 className="form-card-title">{form.name}</h3>
                      <GlassBadge variant={STATUS_VARIANT[form.status] || 'default'} size="sm">
                        {form.status}
                      </GlassBadge>
                    </div>
                    {form.description && (
                      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                        {form.description}
                      </p>
                    )}
                    <div className="form-card-meta">
                      <span className="info-row">
                        <UserPlus size={13} />
                        {form.submissions_count} submission{form.submissions_count !== 1 ? 's' : ''}
                      </span>
                      {form.campaign_name && (
                        <span className="info-row">
                          <GitMerge size={13} />
                          {form.campaign_name}
                        </span>
                      )}
                      <span className="info-row">
                        {form.crm_sync_enabled ? (
                          <GlassBadge variant="success" size="sm">CRM sync on</GlassBadge>
                        ) : (
                          <GlassBadge variant="default" size="sm">CRM sync off</GlassBadge>
                        )}
                      </span>
                    </div>
                    <div className="form-card-actions">
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<Pencil size={14} />}
                        onClick={() => { setEditingForm(form); setActiveTab('builder'); }}
                      >
                        Edit
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<UserPlus size={14} />}
                        onClick={() => { setSelectedFormForSubs(form.form_id); setActiveTab('submissions'); }}
                      >
                        Submissions
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<ClipboardCopy size={14} />}
                        onClick={() => handleCopyEmbed(form)}
                        disabled={!form.embed_code}
                        title="Copy embed code"
                      >
                        {copiedFormId === form.form_id ? 'Copied!' : 'Embed'}
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleToggleFormStatus(form)}
                      >
                        {form.status === 'active' ? 'Deactivate' : 'Activate'}
                      </GlassButton>
                      <GlassButton
                        variant="danger"
                        size="sm"
                        icon={<Trash2 size={14} />}
                        onClick={() => handleDeleteForm(form.form_id)}
                        title="Delete form"
                      />
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Form Builder ───────────────────────────────────────────── */}
      {activeTab === 'builder' && (
        <GlassCard>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', margin: 0 }}>
              {editingForm ? `Edit: ${editingForm.name}` : 'New Form'}
            </h2>
          </div>
          <FormBuilderPanel
            projectId={projectId}
            campaigns={campaigns}
            editingForm={editingForm}
            onSaved={form => { handleFormSaved(form); }}
            onCancel={() => { setEditingForm(null); setActiveTab('forms'); }}
          />
        </GlassCard>
      )}

      {/* ── Tab: Submissions ────────────────────────────────────────────── */}
      {activeTab === 'submissions' && (
        <div className="submissions-container">
          {/* zIndex:10 lifts this card above the sibling submissions table card
              so the GlassSelect dropdown isn't painted behind it. GlassCard has
              backdrop-filter which creates its own stacking context, so the
              dropdown's internal zIndex can't escape on its own. */}
          <GlassCard style={{ position: 'relative', zIndex: 10, overflow: 'visible' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <GlassSelect
                  label="Select Form"
                  options={[{ value: '', label: '— Choose form —' }, ...formOptions]}
                  value={selectedFormForSubs}
                  onChange={v => { setSelectedFormForSubs(v); setSubSkip(0); }}
                />
              </div>
              <GlassButton
                variant="secondary"
                size="sm"
                icon={<RefreshCw size={14} />}
                onClick={loadSubmissions}
                disabled={!selectedFormForSubs}
              >
                Refresh
              </GlassButton>
            </div>
          </GlassCard>

          {!selectedFormForSubs ? (
            <GlassCard>
              <div className="empty-state">
                <UserPlus size={40} className="empty-state-icon" />
                <h3 className="empty-state-title">Select a form</h3>
                <p className="empty-state-desc">Choose a form above to view its submissions.</p>
              </div>
            </GlassCard>
          ) : submissions.length === 0 ? (
            <GlassCard>
              <div className="empty-state">
                <UserPlus size={40} className="empty-state-icon" />
                <h3 className="empty-state-title">No submissions yet</h3>
                <p className="empty-state-desc">Submissions will appear here once visitors fill out the form.</p>
              </div>
            </GlassCard>
          ) : (
            <GlassCard>
              <div className="submissions-table-wrapper">
                <table className="submissions-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Data (preview)</th>
                      <th>CRM Synced</th>
                      <th>IP</th>
                      <th style={{ width: 56, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(sub => {
                      const previewEntries = Object.entries(sub.data || {}).slice(0, 3);
                      const previewText = previewEntries.map(([k, v]) => `${k}: ${v}`).join(' · ') || '—';
                      return (
                        <tr key={sub.submission_id}>
                          <td>{formatDate(sub.submitted_at)}</td>
                          <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {previewText}
                          </td>
                          <td>
                            {sub.crm_lead_id ? (
                              <GlassBadge variant="success" size="sm">Synced</GlassBadge>
                            ) : (
                              <GlassBadge variant="default" size="sm">Not synced</GlassBadge>
                            )}
                          </td>
                          <td>{sub.ip_address || '—'}</td>
                          <td style={{ textAlign: 'right' }}>
                            <GlassButton
                              variant="secondary"
                              size="sm"
                              icon={<Trash2 size={14} />}
                              title="Delete submission"
                              onClick={() => setPendingDelete(sub)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {subTotal > subLimit && (
                <div className="submissions-pagination">
                  <span>{subSkip + 1}–{Math.min(subSkip + subLimit, subTotal)} of {subTotal}</span>
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    disabled={subSkip === 0}
                    onClick={() => setSubSkip(prev => Math.max(0, prev - subLimit))}
                  >
                    Previous
                  </GlassButton>
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    disabled={subSkip + subLimit >= subTotal}
                    onClick={() => setSubSkip(prev => prev + subLimit)}
                  >
                    Next
                  </GlassButton>
                </div>
              )}
            </GlassCard>
          )}

          {/* Delete-submission confirm modal — two-button cascade choice. */}
          <GlassModal
            isOpen={!!pendingDelete}
            onClose={() => { if (!deleting) setPendingDelete(null); }}
            title="Delete submission?"
            size="md"
            footer={
              <>
                <GlassButton
                  variant="secondary"
                  onClick={() => setPendingDelete(null)}
                  disabled={deleting}
                >
                  Cancel
                </GlassButton>
                <GlassButton
                  variant="secondary"
                  loading={deleting}
                  onClick={async () => {
                    if (!pendingDelete) return;
                    setDeleting(true);
                    try {
                      await deleteFormSubmission(pendingDelete.submission_id, false);
                      setSubmissions(prev => prev.filter(s => s.submission_id !== pendingDelete.submission_id));
                      setSubTotal(prev => Math.max(0, prev - 1));
                      setPendingDelete(null);
                    } catch (e) {
                      console.error('Failed to delete submission', e);
                      window.alert('Failed to delete submission. Please try again.');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  Delete from AIME only
                </GlassButton>
                <GlassButton
                  variant="danger"
                  loading={deleting}
                  disabled={!pendingDelete?.crm_lead_id}
                  title={pendingDelete?.crm_lead_id ? '' : 'Submission was never synced to CRM'}
                  onClick={async () => {
                    if (!pendingDelete) return;
                    setDeleting(true);
                    try {
                      const result = await deleteFormSubmission(pendingDelete.submission_id, true);
                      setSubmissions(prev => prev.filter(s => s.submission_id !== pendingDelete.submission_id));
                      setSubTotal(prev => Math.max(0, prev - 1));
                      setPendingDelete(null);
                      if (result.crm_error) {
                        window.alert(`AIME submission deleted, but the CRM lead could not be removed: ${result.crm_error}\n\nDelete it from the CRM manually if needed.`);
                      }
                    } catch (e) {
                      console.error('Failed to delete submission', e);
                      window.alert('Failed to delete submission. Please try again.');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                >
                  Delete from AIME + CRM
                </GlassButton>
              </>
            }
          >
            <p style={{ margin: '0 0 var(--space-3)', color: 'var(--text)' }}>
              You're about to delete this submission:
            </p>
            <div style={{
              padding: 'var(--space-3)',
              background: 'var(--panel-strong)',
              border: '1px solid var(--stroke)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
              wordBreak: 'break-word',
            }}>
              {pendingDelete
                ? (Object.entries(pendingDelete.data || {}).slice(0, 4).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—')
                : ''}
            </div>
            <p style={{ margin: 'var(--space-4) 0 0', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
              {pendingDelete?.crm_lead_id
                ? 'This submission was synced to your CRM. Choose whether to remove the CRM lead at the same time, or keep it (e.g. if your sales team has added notes).'
                : 'This submission was never synced to a CRM, so only the AIME record will be removed.'}
            </p>
          </GlassModal>
        </div>
      )}

      {/* ── Tab: A/B Tests ──────────────────────────────────────────────── */}
      {activeTab === 'abtests' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowABModal(true)}>
              New A/B Test
            </GlassButton>
          </div>

          {abTests.length === 0 ? (
            <GlassCard>
              <div className="empty-state">
                <FlaskConical size={40} className="empty-state-icon" />
                <h3 className="empty-state-title">No A/B tests yet</h3>
                <p className="empty-state-desc">Compare two landing pages to optimise conversion rates.</p>
                <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowABModal(true)}>
                  Create A/B Test
                </GlassButton>
              </div>
            </GlassCard>
          ) : (
            <div className="ab-tests-grid">
              {abTests.map(test => {
                const results = abResults[test.test_id];
                const isExpanded = expandedTestId === test.test_id;
                const rateA = results ? (results.variant_a.rate * 100).toFixed(1) : null;
                const rateB = results ? (results.variant_b.rate * 100).toFixed(1) : null;
                const maxRate = results ? Math.max(results.variant_a.rate, results.variant_b.rate) : 0;
                return (
                  <GlassCard key={test.test_id}>
                    <div className="ab-test-card">
                      <div className="ab-test-header">
                        <h3 className="ab-test-title">{test.name}</h3>
                        <GlassBadge variant={STATUS_VARIANT[test.status] || 'default'} size="sm">
                          {test.status}
                        </GlassBadge>
                      </div>

                      <div className="ab-test-split-bar">
                        <div className="ab-test-split-a" style={{ width: `${test.traffic_split}%` }} />
                        <div className="ab-test-split-b" />
                      </div>

                      <div className="ab-test-variants">
                        <div className="ab-test-variant">
                          <span className="ab-variant-dot a" />
                          <span>A ({test.traffic_split}%): {test.page_a_name || test.landing_page_id_a}</span>
                          {test.variant_a_url && (
                            <>
                              <a href={test.variant_a_url} target="_blank" rel="noopener noreferrer" title={test.variant_a_url} style={{ color: 'var(--primary)', marginLeft: 6 }}>
                                <ExternalLink size={12} />
                              </a>
                              <button
                                className="ab-copy-btn"
                                title="Copy variant A URL"
                                onClick={() => handleCopyAbUrl(test.variant_a_url!, `${test.test_id}-a`)}
                              >
                                {copiedAbUrl === `${test.test_id}-a` ? <CheckCheck size={12} /> : <Copy size={12} />}
                              </button>
                            </>
                          )}
                        </div>
                        <div className="ab-test-variant">
                          <span className="ab-variant-dot b" />
                          <span>B ({100 - test.traffic_split}%): {test.page_b_name || test.landing_page_id_b}</span>
                          {test.variant_b_url && (
                            <>
                              <a href={test.variant_b_url} target="_blank" rel="noopener noreferrer" title={test.variant_b_url} style={{ color: 'var(--primary)', marginLeft: 6 }}>
                                <ExternalLink size={12} />
                              </a>
                              <button
                                className="ab-copy-btn"
                                title="Copy variant B URL"
                                onClick={() => handleCopyAbUrl(test.variant_b_url!, `${test.test_id}-b`)}
                              >
                                {copiedAbUrl === `${test.test_id}-b` ? <CheckCheck size={12} /> : <Copy size={12} />}
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {test.winner && (
                        <GlassBadge variant="success" size="sm">
                          <Trophy size={12} style={{ marginRight: 4 }} />
                          Winner: Variant {test.winner.toUpperCase()}
                        </GlassBadge>
                      )}

                      {/* Live results panel */}
                      {isExpanded && results && (
                        <div className="ab-results-panel">
                          <div className="ab-results-row">
                            <div className="ab-result-variant">
                              <span className="ab-variant-label">
                                <span className="ab-variant-dot a" /> Variant A
                                {results.recommended_winner === 'a' && <Trophy size={12} style={{ color: 'var(--success)', marginLeft: 4 }} />}
                              </span>
                              <div className="ab-stat-row">
                                <span>{results.variant_a.visits} visits</span>
                                <span>{results.variant_a.conversions} conv.</span>
                                <span className="ab-rate">{rateA}%</span>
                              </div>
                              <div className="ab-rate-bar-track">
                                <div
                                  className="ab-rate-bar a"
                                  style={{ width: maxRate > 0 ? `${(results.variant_a.rate / maxRate) * 100}%` : '0%' }}
                                />
                              </div>
                            </div>
                            <div className="ab-result-variant">
                              <span className="ab-variant-label">
                                <span className="ab-variant-dot b" /> Variant B
                                {results.recommended_winner === 'b' && <Trophy size={12} style={{ color: 'var(--success)', marginLeft: 4 }} />}
                              </span>
                              <div className="ab-stat-row">
                                <span>{results.variant_b.visits} visits</span>
                                <span>{results.variant_b.conversions} conv.</span>
                                <span className="ab-rate">{rateB}%</span>
                              </div>
                              <div className="ab-rate-bar-track">
                                <div
                                  className="ab-rate-bar b"
                                  style={{ width: maxRate > 0 ? `${(results.variant_b.rate / maxRate) * 100}%` : '0%' }}
                                />
                              </div>
                            </div>
                          </div>
                          <div className="ab-significance">
                            {results.significant
                              ? <GlassBadge variant="success" size="sm">Statistically significant</GlassBadge>
                              : <GlassBadge variant="warning" size="sm">Need more data ({results.total_visits}/100 visits)</GlassBadge>
                            }
                          </div>
                          {results.significant && results.recommended_winner && test.status !== 'completed' && (
                            <div className="ab-promote-row">
                              <GlassButton
                                variant="primary"
                                size="sm"
                                icon={<Trophy size={14} />}
                                loading={promotingTestId === test.test_id}
                                onClick={() => handlePromoteWinner(test, results.recommended_winner!)}
                              >
                                Promote Variant {results.recommended_winner.toUpperCase()} as Winner
                              </GlassButton>
                            </div>
                          )}
                          {test.status === 'active' && (
                            <div className="ab-auto-promote-row">
                              <label className="ab-auto-promote-label">
                                <input
                                  type="checkbox"
                                  checked={!!test.auto_promote}
                                  onChange={() => handleToggleAutoPromote(test)}
                                />
                                <span>Auto-promote winner when statistically significant</span>
                              </label>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="ab-test-actions">
                        {/* Deploy variants */}
                        {!test.variant_a_url && test.status !== 'completed' && (
                          <GlassButton
                            variant="secondary"
                            size="sm"
                            icon={<Cloud size={14} />}
                            loading={deployingTestId === test.test_id}
                            onClick={() => handleDeployVariants(test)}
                          >
                            Deploy Variants
                          </GlassButton>
                        )}

                        {/* Start test */}
                        {test.status === 'draft' && (
                          <GlassButton
                            variant="primary"
                            size="sm"
                            icon={<Play size={14} />}
                            loading={startingTestId === test.test_id}
                            onClick={() => handleStartTest(test)}
                          >
                            Start Test
                          </GlassButton>
                        )}

                        {/* Legacy start (running/paused) */}
                        {test.status === 'running' && (
                          <>
                            <GlassButton variant="secondary" size="sm" onClick={() => handleABAction(test, 'pause')}>
                              Pause
                            </GlassButton>
                          </>
                        )}
                        {test.status === 'paused' && (
                          <GlassButton variant="primary" size="sm" onClick={() => handleABAction(test, 'start')}>
                            Resume
                          </GlassButton>
                        )}

                        {/* Manual promote buttons for active tests */}
                        {(test.status === 'active' || test.status === 'running') && (
                          <>
                            <GlassButton
                              variant="secondary"
                              size="sm"
                              loading={promotingTestId === test.test_id}
                              onClick={() => handlePromoteWinner(test, 'a')}
                            >
                              Winner: A
                            </GlassButton>
                            <GlassButton
                              variant="secondary"
                              size="sm"
                              loading={promotingTestId === test.test_id}
                              onClick={() => handlePromoteWinner(test, 'b')}
                            >
                              Winner: B
                            </GlassButton>
                          </>
                        )}

                        {/* Results toggle */}
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          icon={<BarChart2 size={14} />}
                          loading={loadingResultsFor === test.test_id}
                          onClick={() => {
                            if (isExpanded) {
                              setExpandedTestId(null);
                            } else {
                              handleLoadResults(test.test_id);
                            }
                          }}
                        >
                          {isExpanded ? 'Hide Results' : 'Results'}
                        </GlassButton>
                      </div>
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Workflows ──────────────────────────────────────────────── */}
      {activeTab === 'workflows' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowWorkflowModal(true)}>
              New Workflow
            </GlassButton>
          </div>

          {workflows.length === 0 ? (
            <GlassCard>
              <div className="empty-state">
                <GitMerge size={40} className="empty-state-icon" />
                <h3 className="empty-state-title">No workflows yet</h3>
                <p className="empty-state-desc">Nurture workflows automate lead follow-up via email sequences.</p>
                <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowWorkflowModal(true)}>
                  Create Workflow
                </GlassButton>
              </div>
            </GlassCard>
          ) : (
            <div className="workflows-grid">
              {workflows.map(wf => (
                <GlassCard key={wf.workflow_id}>
                  <div className="workflow-card">
                    <div className="workflow-card-header">
                      <h3 className="workflow-card-title">{wf.name}</h3>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {isWorkflowNotArmed(wf) && (
                          <span title="This workflow is active but is missing the campaign and/or sequence link its trigger needs, so it can never actually fire.">
                            <GlassBadge variant="warning" size="sm">
                              Not armed
                            </GlassBadge>
                          </span>
                        )}
                        <GlassBadge variant={STATUS_VARIANT[wf.status] || 'default'} size="sm">
                          {wf.status}
                        </GlassBadge>
                      </div>
                    </div>
                    {isWorkflowNotArmed(wf) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', color: 'var(--warning)', fontSize: 'var(--text-xs)' }}>
                        <AlertCircle size={12} /> Not armed — link a campaign and a sequence
                      </div>
                    )}
                    <div className="workflow-card-stat">{wf.leads_enrolled}</div>
                    <div className="workflow-card-meta">
                      <span className="info-row"><Zap size={13} /> Trigger: {wf.trigger.replace(/_/g, ' ')}</span>
                      {wf.email_sequence_name && (
                        <span className="info-row"><GitMerge size={13} /> Sequence: {wf.email_sequence_name}</span>
                      )}
                      {wf.campaign_name && (
                        <span className="info-row"><GitMerge size={13} /> Campaign: {wf.campaign_name}</span>
                      )}
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>leads enrolled</span>
                      <span className="info-row"><History size={13} /> Last fired: {wf.last_triggered_at ? formatDateTime(wf.last_triggered_at) : 'Never fired'}</span>
                    </div>
                    <div className="workflow-card-actions">
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleWorkflowToggle(wf)}
                      >
                        {wf.status === 'active' ? 'Pause' : 'Activate'}
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<Pencil size={14} />}
                        onClick={() => handleOpenEditWorkflow(wf)}
                      >
                        Edit
                      </GlassButton>
                      <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<History size={14} />}
                        onClick={() => handleViewEnrollmentLog(wf)}
                      >
                        Enrollment Log
                      </GlassButton>
                      <GlassButton
                        variant="primary"
                        size="sm"
                        icon={<UserPlus size={14} />}
                        onClick={() => setEnrollWorkflow(wf)}
                      >
                        Enroll Lead
                      </GlassButton>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── A/B Test create modal ────────────────────────────────────────── */}
      {showABModal && (
        <div className="panel-overlay" onClick={() => setShowABModal(false)}>
          <div className="panel-sheet" onClick={e => e.stopPropagation()}>
            <div className="panel-sheet-header">
              <h2 className="panel-sheet-title">New A/B Test</h2>
              <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={() => setShowABModal(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <GlassInput label="Test Name *" value={abName} onChange={setAbName} placeholder="Homepage variant test" />
              <GlassSelect label="Variant A (Landing Page) *" options={pageOptions} value={abPageA} onChange={setAbPageA} />
              <GlassSelect label="Variant B (Landing Page) *" options={pageOptions} value={abPageB} onChange={setAbPageB} />
              <div>
                <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--text-secondary)', display: 'block', marginBottom: 'var(--space-1)' }}>
                  Traffic Split — Variant A: {abSplit}% / Variant B: {100 - abSplit}%
                </label>
                <div className="split-input-wrapper">
                  <span className="split-label">0%</span>
                  <input
                    type="range"
                    min={10}
                    max={90}
                    step={5}
                    value={abSplit}
                    onChange={e => setAbSplit(Number(e.target.value))}
                  />
                  <span className="split-label">100%</span>
                </div>
              </div>
              {abError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
                  <AlertCircle size={14} /> {abError}
                </div>
              )}
            </div>
            <div className="panel-sheet-footer">
              <GlassButton variant="secondary" onClick={() => setShowABModal(false)}>Cancel</GlassButton>
              <GlassButton variant="primary" loading={abSaving} icon={<FlaskConical size={16} />} onClick={handleCreateABTest}>
                Create Test
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Workflow create modal ─────────────────────────────────────────── */}
      {showWorkflowModal && (
        <div className="panel-overlay" onClick={() => setShowWorkflowModal(false)}>
          <div className="panel-sheet" onClick={e => e.stopPropagation()}>
            <div className="panel-sheet-header">
              <h2 className="panel-sheet-title">New Nurture Workflow</h2>
              <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={() => setShowWorkflowModal(false)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <GlassInput label="Workflow Name *" value={wfName} onChange={setWfName} placeholder="Post-form follow-up" />
              <GlassInput label="Description" value={wfDescription} onChange={setWfDescription} placeholder="Brief description" />
              <GlassSelect label="Trigger" options={TRIGGER_OPTIONS} value={wfTrigger} onChange={setWfTrigger} />
              <GlassSelect label="Link Email Sequence (optional)" options={seqOptions} value={wfSeqId} onChange={setWfSeqId} />
              <GlassSelect label="Link Campaign (optional)" options={campaignOptions} value={wfCampaignId} onChange={setWfCampaignId} />
              {wfError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
                  <AlertCircle size={14} /> {wfError}
                </div>
              )}
            </div>
            <div className="panel-sheet-footer">
              <GlassButton variant="secondary" onClick={() => setShowWorkflowModal(false)}>Cancel</GlassButton>
              <GlassButton variant="primary" loading={wfSaving} icon={<GitMerge size={16} />} onClick={handleCreateWorkflow}>
                Create Workflow
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Workflow edit modal ───────────────────────────────────────────── */}
      {editingWorkflow && (
        <div className="panel-overlay" onClick={() => setEditingWorkflow(null)}>
          <div className="panel-sheet" onClick={e => e.stopPropagation()}>
            <div className="panel-sheet-header">
              <h2 className="panel-sheet-title">Edit Workflow — {editingWorkflow.name}</h2>
              <GlassButton variant="ghost" size="sm" icon={<X size={16} />} onClick={() => setEditingWorkflow(null)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <GlassInput label="Workflow Name *" value={ewName} onChange={setEwName} placeholder="Post-form follow-up" />
              <GlassInput label="Description" value={ewDescription} onChange={setEwDescription} placeholder="Brief description" />
              <GlassSelect label="Trigger" options={TRIGGER_OPTIONS} value={ewTrigger} onChange={setEwTrigger} />
              <GlassSelect label="Link Email Sequence (optional)" options={seqOptions} value={ewSeqId} onChange={setEwSeqId} />
              <GlassSelect label="Link Campaign (optional)" options={campaignOptions} value={ewCampaignId} onChange={setEwCampaignId} />
              <GlassSelect
                label="Status"
                options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                ]}
                value={ewStatus}
                onChange={setEwStatus}
              />
              {FIRING_TRIGGERS.includes(ewTrigger) && (!ewCampaignId || !ewSeqId) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--warning)', fontSize: 'var(--text-sm)' }}>
                  <AlertCircle size={14} /> This trigger needs both a campaign and a sequence to fire — saving now will leave it "Not armed".
                </div>
              )}
              {ewError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
                  <AlertCircle size={14} /> {ewError}
                </div>
              )}
            </div>
            <div className="panel-sheet-footer">
              <GlassButton variant="secondary" onClick={() => setEditingWorkflow(null)}>Cancel</GlassButton>
              <GlassButton variant="primary" loading={ewSaving} icon={<GitMerge size={16} />} onClick={handleUpdateWorkflow}>
                Save Changes
              </GlassButton>
            </div>
          </div>
        </div>
      )}

      {/* ── Enroll lead modal ─────────────────────────────────────────────── */}
      {enrollWorkflow && (
        <EnrollModal
          workflow={enrollWorkflow}
          onClose={() => setEnrollWorkflow(null)}
          onEnrolled={loadWorkflows}
        />
      )}

      {/* ── Enrollment log panel ────────────────────────────────────────────
          Read-only observability: last 50 automatic enrollment attempts for
          this workflow (VIEW-level gate on the backend, so a viewer without
          edit access can still see this). recipient_email is PII — shown
          plainly here, same as everywhere else in this module (e.g. the
          Submissions tab), no additional masking beyond the access gate. */}
      <GlassModal
        isOpen={!!logWorkflow}
        onClose={() => setLogWorkflow(null)}
        title={logWorkflow ? `Enrollment Log — ${logWorkflow.name}` : 'Enrollment Log'}
        size="lg"
      >
        {logLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-4)', color: 'var(--text-secondary)' }}>
            <Loader2 size={16} /> Loading enrollment log…
          </div>
        ) : logError ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
            <AlertCircle size={14} /> {logError}
          </div>
        ) : logEvents.length === 0 ? (
          <div className="empty-state">
            <History size={32} className="empty-state-icon" />
            <p className="empty-state-desc">No enrollment attempts recorded yet for this workflow.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Recipient</th>
                  <th>Outcome</th>
                  <th>Source</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {logEvents.map(ev => (
                  <tr key={ev.event_id}>
                    <td>{formatDateTime(ev.created_at)}</td>
                    <td>{ev.recipient_email}</td>
                    <td>
                      <GlassBadge variant={OUTCOME_VARIANT[ev.outcome] || 'default'} size="sm">
                        {ev.outcome.replace(/_/g, ' ')}
                      </GlassBadge>
                    </td>
                    <td>{ev.source.replace(/_/g, ' ')}</td>
                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>{ev.detail || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassModal>
    </div>
  );
};

export default LeadCaptureModule;
